package org.teddapp.project.ui.login

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.teddapp.project.auth.BiometricAuthenticator
import org.teddapp.project.auth.BiometricResult
import org.teddapp.project.db.repo.AccountRepository
import org.teddapp.project.db.repo.LoginResult
import org.teddapp.project.db.repo.SignupResult

internal enum class LoginFlow { Login, Signup }
internal enum class LoginMode { Biometric, Password }
internal enum class LoginScanState { Idle, Scanning, Success, Failed }

/**
 * État de l'écran de connexion, commun à toutes les plateformes.
 *
 * Deux parcours dictés par la base : aucun compte → création, compte présent → connexion.
 * La biométrie n'est proposée que si un authentificateur est fourni, que l'appareil la
 * supporte et que le compte l'a enrôlée.
 */
internal class LoginModel(
    private val accounts: AccountRepository,
    private val biometric: BiometricAuthenticator?,
    private val scope: CoroutineScope,
    private val onLoginSuccess: () -> Unit,
    /** Tente la connexion au serveur avec ces identifiants ; vrai s'il les accepte. Null : pas de serveur. */
    private val serverLogin: (suspend (email: String, password: String) -> Boolean)? = null,
    /**
     * Crée aussi le compte sur le serveur à la création locale : c'est lui qui relie les
     * appareils. Rend un mot pour l'écran si le serveur refuse ; null quand tout va bien.
     */
    private val serverSignup: (suspend (email: String, password: String) -> String?)? = null,
) {
    /** Ce que le serveur a dit à la création du compte, s'il n'a pas pu le créer. */
    var serverNotice by mutableStateOf<String?>(null)
    var flow by mutableStateOf(LoginFlow.Login)
    var mode by mutableStateOf(LoginMode.Password)
    var biometricAvailable by mutableStateOf(false)
    var initialized by mutableStateOf(false)
    var email by mutableStateOf("")
    var password by mutableStateOf("")
    var passwordConfirm by mutableStateOf("")
    var showPwd by mutableStateOf(false)
    var errorMessage by mutableStateOf<String?>(null)
    var scanState by mutableStateOf(LoginScanState.Idle)
    var busy by mutableStateOf(false)

    val isSignup: Boolean get() = flow == LoginFlow.Signup
    val showBiometric: Boolean get() = flow == LoginFlow.Login && mode == LoginMode.Biometric && biometricAvailable
    val passwordsMatch: Boolean get() = password.isNotEmpty() && password == passwordConfirm
    val canSubmit: Boolean
        get() = initialized && !busy && email.isNotBlank() && password.isNotEmpty() && (!isSignup || passwordsMatch)

    suspend fun init() {
        val current = accounts.current()
        if (current == null) {
            flow = LoginFlow.Signup
            biometricAvailable = false
        } else {
            flow = LoginFlow.Login
            email = current.email
            biometricAvailable = current.biometric_enrolled == 1L && biometric?.isAvailable() == true
            mode = if (biometricAvailable) LoginMode.Biometric else LoginMode.Password
        }
        initialized = true
    }

    fun editEmail(v: String) { email = v; errorMessage = null }
    fun editPassword(v: String) { password = v; errorMessage = null }
    fun editPasswordConfirm(v: String) { passwordConfirm = v; errorMessage = null }

    fun usePassword() { mode = LoginMode.Password; scanState = LoginScanState.Idle; errorMessage = null }
    fun useBiometric() { mode = LoginMode.Biometric; errorMessage = null }

    fun submit() {
        if (busy || !canSubmit) return
        errorMessage = null
        scope.launch {
            busy = true
            try {
                if (isSignup) {
                    when (accounts.signup(email.trim(), password)) {
                        SignupResult.Success -> {
                            // Le compte serveur suit : sans lui, les données restent sur cet appareil.
                            serverNotice = serverSignup?.invoke(email.trim(), password)
                            onLoginSuccess()
                        }
                        SignupResult.AlreadyHasAccount -> errorMessage = "Un compte existe déjà sur cet appareil."
                    }
                } else {
                    when (accounts.loginWithPassword(email.trim(), password)) {
                        is LoginResult.Success -> onLoginSuccess()
                        // Pas ce compte-là sur l'appareil : si c'est le compte serveur, il
                        // déverrouille aussi, et l'appareil adopte ses identifiants. Un seul
                        // mot de passe à retenir, pas deux écrans.
                        LoginResult.NoSuchAccount, LoginResult.WrongPassword -> {
                            val accepted = serverLogin?.invoke(email.trim(), password) ?: false
                            if (accepted && accounts.adopt(email.trim(), password) != null) onLoginSuccess()
                            else errorMessage = if (serverLogin == null) "Aucun compte avec cet email." else "Aucun compte avec cet email, ni sur cet appareil ni sur le serveur."
                        }
                        LoginResult.BiometricNotEnrolled -> Unit
                    }
                }
            } finally {
                busy = false
            }
        }
    }

    fun triggerBiometric() {
        val auth = biometric ?: return
        if (scanState != LoginScanState.Idle) return
        errorMessage = null
        scanState = LoginScanState.Scanning
        scope.launch {
            when (auth.authenticate("Déverrouille teddApp")) {
                BiometricResult.Success -> when (accounts.loginWithBiometric()) {
                    is LoginResult.Success -> {
                        scanState = LoginScanState.Success
                        delay(700)
                        onLoginSuccess()
                    }
                    else -> {
                        scanState = LoginScanState.Failed
                        errorMessage = "Compte introuvable."
                    }
                }
                BiometricResult.Cancelled -> scanState = LoginScanState.Idle
                BiometricResult.NotAvailable -> {
                    scanState = LoginScanState.Idle
                    errorMessage = "Biométrie indisponible sur cet appareil."
                    biometricAvailable = false
                    mode = LoginMode.Password
                }
                is BiometricResult.Error -> {
                    scanState = LoginScanState.Failed
                    errorMessage = "Échec : essaie le mot de passe."
                }
            }
        }
    }
}

@Composable
internal fun rememberLoginModel(
    accounts: AccountRepository,
    biometric: BiometricAuthenticator?,
    onLoginSuccess: () -> Unit,
): LoginModel {
    val scope = rememberCoroutineScope()
    val session = org.teddapp.project.server.LocalServerSession.current
    val serverLogin: (suspend (String, String) -> Boolean)? = session?.let { s ->
        { email, password ->
            if (s.connected) true.also { if (!s.email.equals(email, ignoreCase = true)) runCatching { s.signIn(s.url, email, password) }.getOrNull() ?: Unit }
            else runCatching { s.signIn(s.url, email, password); true }.getOrDefault(false)
        }
    }
    val serverSignup: (suspend (String, String) -> String?)? = session?.takeIf { it.url.isNotBlank() }?.let { s ->
        { email, password ->
            try {
                if (s.connected) null else { s.signUp(s.url, email, password); null }
            } catch (e: org.teddapp.project.server.ServerException) {
                when (e.code) {
                    "signup_closed" -> "Compte créé sur cet appareil. Pour le relier à tes autres appareils, demande ton compte serveur dans les Réglages."
                    "email_taken" -> if (runCatching { s.signIn(s.url, email, password); true }.getOrDefault(false)) null else "Un compte serveur existe déjà avec cette adresse, avec un autre mot de passe : connecte-le dans les Réglages."
                    else -> null
                }
            }
        }
    }
    val model = remember(accounts, biometric, session) { LoginModel(accounts, biometric, scope, onLoginSuccess, serverLogin, serverSignup) }
    LaunchedEffect(model) { model.init() }
    return model
}
